<template>
    <VJoyDropdown
        class="portfolio-filter-dropdown"
        :close-on-select="false"
        :disable-teleport="true"
        width="320"
        justify="right"
        data-testid="portfolio-filter-dropdown"
    >
        <template #dropdown-button>
            <VJoyButton variant="secondary" icon="filter" size="xsmall" data-testid="portfolio-filter-dropdown-trigger">
                {{ triggerLabel }}
            </VJoyButton>
        </template>
        <template #dropdown-items>
            <VJoyCheckbox
                v-for="category in availableCategories"
                :key="category"
                :checked="selectedCategories.includes(category)"
                :label="t(`profile.section.portfolio.editor.publish.dialog.category.${category}`)"
                @update:checked="(checked) => toggleCategory(category, checked)"
            />
        </template>
    </VJoyDropdown>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {useTranslation} from '#imports';
    import {VJoyButton, VJoyCheckbox, VJoyDropdown} from '@maltjoy/core-vue';
    import type {PortfolioCategory} from '@api';

    interface Props {
        availableCategories: PortfolioCategory[];
    }

    const {availableCategories} = defineProps<Props>();

    const selectedCategories = defineModel<PortfolioCategory[]>({default: () => []});

    const {t} = useTranslation();

    const triggerLabel = computed(() =>
        selectedCategories.value.length > 0
            ? t('profile.section.portfolio.filter.label', [selectedCategories.value.length])
            : t('profile.section.portfolio.filter.button.label'),
    );

    function addCategory(category: PortfolioCategory) {
        selectedCategories.value = [...selectedCategories.value, category];
    }

    function removeCategory(category: PortfolioCategory) {
        selectedCategories.value = selectedCategories.value.filter((selectedCategory) => selectedCategory !== category);
    }

    function toggleCategory(category: PortfolioCategory, checked: boolean) {
        if (checked) {
            addCategory(category);
            return;
        }
        removeCategory(category);
    }
</script>

<style lang="scss" scoped>
    :deep(.joy-dropdown-list__items-slotted) {
        padding: 0 !important;
    }

    :deep(.joy-dropdown-list .joy-checkbox) {
        padding: 10px var(--joy-core-spacing-4) 14px;
        margin-bottom: 0;
        min-height: 48px;
        align-items: center;
        box-sizing: border-box;
    }

    :deep(.joy-dropdown-list .joy-checkbox.joy-checkbox__checked) {
        background-color: var(--joy-color-secondary-10);

        --checkbox-color: var(--joy-color-secondary-50);
        --checkbox-font-weight: var(--joy-font-weight-normal);
    }
</style>
